import { useEffect, useState } from 'react';
import {
  AlertBanner,
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  ProcessingBasisNote,
  ProgressBar,
  Select,
  Toast,
} from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, endpoints, type MeetingDetail } from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';
import { formatDateTime } from '../lib/format.js';

/**
 * RAPAT HARIAN 10 MENIT — Figma `17:2` · M07 · rute `/meetings/daily` · izin LDR MGR FAC.
 *
 * LIMA KONDISI PETA §5.3, SEMUANYA DIBANGUN
 * -----------------------------------------
 * belum mulai · berjalan (timer) · lewat waktu · selesai · tanpa peserta.
 *
 * TIMER-NYA MENGHITUNG DARI `started_at` SERVER, BUKAN DARI SAAT LAYAR DIBUKA
 * --------------------------------------------------------------------------
 * Ini bukan detail implementasi. Sebuah timer yang mulai dari nol ketika layar dimuat akan
 * menunjukkan "00:12" pada rapat yang sudah berjalan sembilan menit — dan seluruh guna
 * kotak waktu 10 menit adalah bahwa semua orang di ruangan melihat angka YANG SAMA. Yang
 * dihitung di klien hanyalah detik yang berjalan sejak jawaban terakhir; `elapsed_seconds`
 * dari server adalah titik nolnya.
 *
 * "LEWAT WAKTU" ADALAH STATE, DAN IA BUKAN URUSAN KLIEN
 * ---------------------------------------------------
 * Transisi `TIMEBOX_ELAPSED` berizin `SYSTEM` di alur kerja: ia milik job
 * `M07_HUDDLE_TIMEBOX`, bukan milik layar. Jadi ketika timer melewati kotak waktunya,
 * layar ini menandainya SECARA VISUAL (bilah kemajuan menjadi peringatan) dan TIDAK
 * mengirim transisi apa pun. Sebuah klien yang memindahkan status karena jam di
 * perambannya lewat adalah klien yang menulis status — dan itu tepat yang satu pintu
 * transisi ada untuk mencegah.
 */
export function DailyHuddle({ onOpenFinding }: { onOpenFinding: (code: string) => void }) {
  const { t, locale, translator } = useSession();
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useResource(async () => (await endpoints.meetings()).items, []);
  const detail = useResource(
    async () => (openId ? endpoints.meeting(openId) : null),
    [openId],
    { enabled: openId !== null },
  );

  // Rapat yang sedang BERJALAN dibuka otomatis: ia satu-satunya yang orang di ruangan
  // sedang lihat, dan menuntut satu klik lagi untuk mencapainya adalah satu klik di
  // tengah rapat sepuluh menit.
  useEffect(() => {
    if (openId !== null) return;
    const running = (list.data ?? []).find((m) => m.state === 'RUNNING' || m.state === 'OVERRUN');
    if (running) setOpenId(running.id);
  }, [list.data, openId]);

  const [creating, setCreating] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newItemFor, setNewItemFor] = useState<string | null>(null);

  async function transisi(meetingId: string, transition: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.meetingTransition(meetingId, { transition });
      await Promise.all([list.reload(), detail.reload()]);
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function tandaiDibahas(meetingId: string, itemId: string, outcome: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.patchMeetingItem(meetingId, itemId, {
        discussed: true,
        outcome_code: outcome,
      });
      await detail.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function tambahButir(meetingId: string, title: string, itemId?: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.addMeetingItem(meetingId, { title, item_id: itemId ?? null });
      setNewTitle('');
      setNewItemFor(null);
      await detail.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen
      title={t('huddle.title')}
      subtitle={t('huddle.subtitle')}
      resource={list}
      moduleCode="M07"
      actions={
        <Button variant="primary" onClick={() => setCreating(true)}>
          {t('huddle.schedule')}
        </Button>
      }
    >
      {(meetings) => (
        <>
          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}

          {meetings.length === 0 ? (
            <EmptyState
              title={t('huddle.empty.title')}
              body={t('huddle.empty.body')}
              action={{ label: t('huddle.schedule'), onClick: () => setCreating(true) }}
            />
          ) : (
            <div className="filters">
              {meetings.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={openId === m.id ? 'is-active' : ''}
                  aria-pressed={openId === m.id}
                  onClick={() => setOpenId(m.id)}
                >
                  {formatDateTime(m.scheduled_at, locale)} · {t(`huddle.state.${m.state}`)}
                </button>
              ))}
            </div>
          )}

          {openId !== null && detail.status === 'ready' && detail.data ? (
            <HuddlePanel
              meeting={detail.data}
              busy={busy}
              onTransition={(code) => void transisi(openId, code)}
              onDiscussed={(itemId, outcome) => void tandaiDibahas(openId, itemId, outcome)}
              onAddCandidate={(title, itemId) => void tambahButir(openId, title, itemId)}
              onOpenFinding={onOpenFinding}
              onAddFreeItem={() => setNewItemFor(openId)}
            />
          ) : null}

          <Dialog
            open={newItemFor !== null}
            kind="confirm"
            title={t('huddle.item.add')}
            description={t('huddle.item.add.describe')}
            confirmLabel={t('app.save')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            confirmDisabled={newTitle.trim().length < 2}
            onConfirm={() => {
              if (newItemFor) void tambahButir(newItemFor, newTitle.trim());
            }}
            onCancel={() => {
              setNewItemFor(null);
              setNewTitle('');
            }}
          >
            <Input
              label={t('huddle.item.title')}
              value={newTitle}
              maxLength={200}
              onChange={(e) => setNewTitle(e.target.value)}
            />
          </Dialog>

          <ScheduleDialog
            open={creating}
            onClose={() => setCreating(false)}
            onDone={async (id) => {
              setCreating(false);
              setToast(t('huddle.scheduled'));
              await list.reload();
              setOpenId(id);
            }}
          />

          {toast ? (
            <div className="assist-toast-stack">
              <Toast
                kind="success"
                signalWord={t('signal.success')}
                title={toast}
                onDismiss={() => setToast(null)}
                closeLabel={t('app.dismiss')}
              />
            </div>
          ) : null}
        </>
      )}
    </Screen>
  );
}

const OUTCOMES = ['RESOLVED', 'CARRIED_OVER', 'ACTION_CREATED', 'NOTED'] as const;

function HuddlePanel({
  meeting,
  busy,
  onTransition,
  onDiscussed,
  onAddCandidate,
  onAddFreeItem,
  onOpenFinding,
}: {
  meeting: MeetingDetail;
  busy: boolean;
  onTransition: (code: string) => void;
  onDiscussed: (itemId: string, outcome: string) => void;
  onAddCandidate: (title: string, itemId: string) => void;
  onAddFreeItem: () => void;
  onOpenFinding: (code: string) => void;
}) {
  const { t, locale } = useSession();

  /**
   * Detik yang berjalan, di ATAS `elapsed_seconds` server.
   *
   * Titik nolnya selalu jawaban server; yang ditambahkan di sini hanyalah waktu sejak
   * jawaban itu diterima. Jadi dua peramban yang membuka rapat yang sama menunjukkan angka
   * yang sama, terlepas dari kapan masing-masing dibuka.
   */
  const [tick, setTick] = useState(0);
  useEffect(() => {
    setTick(0);
    if (meeting.state !== 'RUNNING' && meeting.state !== 'OVERRUN') return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [meeting.id, meeting.state, meeting.elapsed_seconds]);

  const elapsed = (meeting.elapsed_seconds ?? 0) + tick;
  const lewatWaktu = elapsed > meeting.timebox_seconds;
  const discussed = meeting.items.filter((i) => i.discussed_at !== null).length;

  return (
    <>
      <section className="card">
        <div className="page__head">
          <div>
            <h2 className="card__title">
              {formatDateTime(meeting.scheduled_at, locale)}{' '}
              <Badge badgeTone={toneOfMeeting(meeting.state)}>
                {t(`huddle.state.${meeting.state}`)}
              </Badge>
              {/* Faset "tanpa peserta" — kondisi turunan, bukan state. */}
              {meeting.facets.includes('NO_ATTENDEES') ? (
                <>
                  {' '}
                  <Badge tone="warning">{t('huddle.facet.NO_ATTENDEES')}</Badge>
                </>
              ) : null}
            </h2>
            <p className="page__subtitle">
              {t('huddle.facilitator', { name: meeting.facilitator.full_name ?? '—' })}
            </p>
          </div>
          <div className="page__actions">
            {meeting.available_transitions.map((tr) => (
              <Button
                key={tr.code}
                variant={tr.code === 'CLOSE' ? 'primary' : 'secondary'}
                busy={busy}
                disabled={tr.blocked_by !== null}
                onClick={() => onTransition(tr.code)}
              >
                {locale === 'id-ID' ? tr.label_id : tr.label_en}
              </Button>
            ))}
          </div>
        </div>

        {/*
          TIMER. Ia bilah kemajuan dan ANGKA sekaligus — sebuah bilah yang penuh tidak
          memberi tahu berapa menit yang sudah lewat, dan warna saja tidak pernah membawa
          arti (peta §3).
        */}
        {meeting.state === 'RUNNING' || meeting.state === 'OVERRUN' ? (
          <>
            <ProgressBar
              value={Math.min(elapsed, meeting.timebox_seconds)}
              max={meeting.timebox_seconds}
              label={t('huddle.timer.label')}
              tone={lewatWaktu ? 'warning' : 'default'}
            />
            <p className={lewatWaktu ? 'huddle__timer is-over' : 'huddle__timer'} role="status">
              {t(lewatWaktu ? 'huddle.timer.over' : 'huddle.timer.running', {
                elapsed: mmss(elapsed),
                box: mmss(meeting.timebox_seconds),
              })}
            </p>
          </>
        ) : null}

        {/* Alasan tombol yang mati, dari server — bukan disimpulkan layar (UAT D-04). */}
        {meeting.available_transitions
          .filter((tr) => tr.blocked_by !== null)
          .map((tr) => (
            <p key={tr.code} className="actions__blocked">
              {t(`error.${tr.blocked_by}`)}
            </p>
          ))}
      </section>

      <section className="card">
        <div className="page__head">
          <h2 className="card__title">
            {t('huddle.agenda', { discussed, total: meeting.items.length })}
          </h2>
          <Button variant="secondary" onClick={onAddFreeItem}>
            {t('huddle.item.add')}
          </Button>
        </div>

        {meeting.items.length === 0 ? (
          <p className="card__muted">{t('huddle.agenda.empty')}</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">{t('huddle.item.title')}</th>
                <th scope="col">{t('huddle.item.finding')}</th>
                <th scope="col">{t('huddle.item.outcome')}</th>
              </tr>
            </thead>
            <tbody>
              {meeting.items.map((item) => (
                <tr key={item.id}>
                  <td>{item.seq}</td>
                  <td>{item.title}</td>
                  <td>
                    {item.item_code ? (
                      <button
                        className="linkish"
                        type="button"
                        onClick={() => onOpenFinding(item.item_code!)}
                      >
                        {item.item_code}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {item.discussed_at !== null ? (
                      <Badge badgeTone="SUCCESS">
                        {t(`huddle.outcome.${item.outcome_code ?? 'NOTED'}`)}
                      </Badge>
                    ) : meeting.state === 'CLOSED' ? (
                      <span className="card__muted">{t('huddle.item.notDiscussed')}</span>
                    ) : (
                      <Select
                        label={t('huddle.item.outcome')}
                        value=""
                        onChange={(e) => {
                          if (e.target.value) onDiscussed(item.id, e.target.value);
                        }}
                        options={[
                          { value: '', label: t('huddle.item.markDiscussed') },
                          ...OUTCOMES.map((o) => ({ value: o, label: t(`huddle.outcome.${o}`) })),
                        ]}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/*
          CALON AGENDA — butir terbuka yang paling lama menunggu, dari server. Ia TIDAK
          ditambahkan otomatis: sebuah agenda yang diisi mesin membuat guard "sekurangnya
          satu butir dibahas" lulus dengan menandai butir yang kebetulan di atas.
        */}
        {meeting.state !== 'CLOSED' && meeting.agenda_candidates.length > 0 ? (
          <>
            <h3>{t('huddle.candidates')}</h3>
            <ul className="candidates">
              {meeting.agenda_candidates.slice(0, 5).map((c) => (
                <li key={c.id}>
                  <span>
                    {c.code} — {c.title}
                    {c.facets.includes('OVERDUE') ? (
                      <>
                        {' '}
                        <Badge tone="danger">{t('facet.OVERDUE')}</Badge>
                      </>
                    ) : null}
                  </span>
                  <Button variant="text" onClick={() => onAddCandidate(c.title, c.id)}>
                    {t('huddle.candidates.add')}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </section>

      <section className="card">
        <h2 className="card__title">
          {t('huddle.attendance', {
            present: meeting.attendance.filter((a) => a.present).length,
            total: meeting.attendance.length,
          })}
        </h2>
        {meeting.attendance.length === 0 ? (
          <p className="card__muted">{t('huddle.attendance.none')}</p>
        ) : (
          <ul className="attendance">
            {meeting.attendance.map((a) => (
              <li key={a.user.id}>
                <Badge badgeTone={a.present ? 'SUCCESS' : 'NEUTRAL'}>
                  {t(a.present ? 'huddle.attendance.present' : 'huddle.attendance.absent')}
                </Badge>{' '}
                {a.user.full_name ?? '—'}
              </li>
            ))}
          </ul>
        )}

        {/* Notulen: dikatakan apa adanya. Pekerja yang membangkitkannya belum ada. */}
        {meeting.state === 'CLOSED' ? (
          <p className="card__muted">
            {meeting.minutes_url ? t('huddle.minutes.ready') : t('huddle.minutes.pending')}
          </p>
        ) : null}

        {meeting.processing_basis ? (
          <ProcessingBasisNote
            label={t('privacy.basisNote.label')}
            sentence={t('privacy.basisNote.sentence', {
              basis: t(`privacy.basis.${meeting.processing_basis.legal_basis}`),
            })}
            article={meeting.processing_basis.article}
            purposes={t('privacy.basisNote.purposes', {
              purposes: meeting.processing_basis.purposes
                .map((p) => t(`privacy.purpose.${p}`))
                .join(', '),
            })}
            moreLabel={t('privacy.basisNote.more')}
            noticeLink={
              <a className="link" href="/privacy-notice">
                {t('privacy.notice.link')}
              </a>
            }
          />
        ) : null}
      </section>
    </>
  );
}

function ScheduleDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (id: string) => Promise<void>;
}) {
  const { t, translator } = useSession();
  const units = useResource(async () => (await endpoints.orgUnits()).items, [], { enabled: open });
  const [unit, setUnit] = useState('');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);

  async function simpan() {
    setBusy(true);
    setProblem(null);
    try {
      const created = await endpoints.createMeeting({
        org_unit_id: unit,
        scheduled_at: new Date(when).toISOString(),
      });
      await onDone(created.id);
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      kind="confirm"
      title={t('huddle.schedule')}
      description={t('huddle.schedule.describe')}
      confirmLabel={t('app.save')}
      cancelLabel={t('app.cancel')}
      busy={busy}
      confirmDisabled={unit === '' || when === ''}
      onConfirm={() => void simpan()}
      onCancel={onClose}
    >
      {problem ? (
        <AlertBanner
          kind="danger"
          signalWord={t('signal.danger')}
          title={renderProblem(translator, problem.problem)}
        />
      ) : null}
      <Select
        label={t('huddle.schedule.unit')}
        value={unit}
        onChange={(e) => setUnit(e.target.value)}
        options={[
          { value: '', label: t('report.field.choose') },
          ...(units.data ?? []).map((u) => ({ value: u.id, label: `${u.code} — ${u.name}` })),
        ]}
      />
      <Input
        label={t('huddle.schedule.when')}
        inputType="datetime-local"
        value={when}
        onChange={(e) => setWhen(e.target.value)}
      />
    </Dialog>
  );
}

function mmss(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function toneOfMeeting(state: string): string {
  switch (state) {
    case 'CLOSED':
      return 'SUCCESS';
    case 'OVERRUN':
      return 'WARNING';
    case 'RUNNING':
      return 'INFORMATION';
    default:
      return 'NEUTRAL';
  }
}
